/**
 * SchoolLogo Component
 *
 * Reusable component for displaying a school's uploaded logo.
 * Handles graceful fallback when no logo is uploaded or if loading fails.
 *
 * @param logoFileId - The file ID of the uploaded logo (from tenant.settings.branding.logoFileId)
 * @param schoolName - School name for alt text
 * @param size - Preset size: 'sm' (header), 'md' (auth forms), 'lg' (dashboard)
 */

interface SchoolLogoProps {
  logoFileId?: string | null;
  schoolName: string;
  size?: 'sm' | 'md' | 'lg';
  style?: React.CSSProperties;
}

const sizeStyles: Record<'sm' | 'md' | 'lg', React.CSSProperties> = {
  sm: { maxHeight: '36px', maxWidth: '120px' },
  md: { maxHeight: '80px', maxWidth: '200px' },
  lg: { maxHeight: '100px', maxWidth: '250px' },
};

export const SchoolLogo: React.FC<SchoolLogoProps> = ({ logoFileId, schoolName, size = 'md', style }) => {
  if (!logoFileId) return null;

  return (
    <img
      src={`/api/files/${logoFileId}`}
      alt={`${schoolName} logo`}
      style={{
        objectFit: 'contain',
        ...sizeStyles[size],
        ...style,
      }}
      onError={(e) => {
        // Hide the image if it fails to load
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );
};
